import React from 'react';

interface SuccessMessageProps {
    action: 'approve' | 'decline';
    reason?: string;
    proposalTitle: string;
}

const SuccessMessage: React.FC<SuccessMessageProps> = ({ action, reason, proposalTitle }) => {
    const isApproved = action === 'approve';
    
    // Set page title based on action
    React.useEffect(() => {
        document.title = `Proposal ${isApproved ? 'Approved' : 'Declined'} - Cinch`;
    }, [isApproved]);
    
    return (
        <div className="min-h-screen bg-gradient-to-br from-cinch-light to-white flex flex-col items-center justify-center px-4">
            <div className="max-w-lg w-full text-center animate-fade-in">
                <div className="mb-8">
                    <img 
                        src="https://cleancinch.com/wp-content/uploads/2025/05/aa836ec1-54ef-4339-9b94-8b6eec324faa.png" 
                        alt="Cinch Logo" 
                        className="h-16 w-auto mx-auto mb-4"
                    />
                </div>
                
                <div className="mb-6">
                    <div className={`inline-flex items-center justify-center w-24 h-24 rounded-full mb-4 ${
                        isApproved ? 'bg-green-100' : 'bg-red-100'
                    }`}>
                        {isApproved ? (
                            <svg className="w-12 h-12 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                        ) : (
                            <svg className="w-12 h-12 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        )}
                    </div>
                </div>

                <h1 className={`text-4xl font-bold mb-4 ${
                    isApproved ? 'text-green-700' : 'text-red-700'
                }`}>
                    {isApproved ? 'Proposal Approved!' : 'Proposal Declined'}
                </h1>
                
                <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                    {isApproved 
                        ? 'Thank you for approving this proposal. We will be in touch soon to begin the next steps.'
                        : 'Thank you for your response. We appreciate your feedback and will use it to improve our future proposals.'
                    }
                </p>

                <div className="bg-white rounded-lg shadow-lg p-6 border border-gray-100 mb-6">
                    <h3 className="text-lg font-semibold text-cinch-primary mb-3">Response Summary</h3>
                    <div className="text-left space-y-3">
                        <div>
                            <span className="text-sm font-medium text-gray-700">Proposal:</span>
                            <p className="text-gray-900">{proposalTitle}</p>
                        </div>
                        <div>
                            <span className="text-sm font-medium text-gray-700">Status:</span>
                            <p className={`font-medium ${isApproved ? 'text-green-600' : 'text-red-600'}`}>
                                {isApproved ? 'Approved' : 'Declined'}
                            </p>
                        </div>
                        {reason && (
                            <div>
                                <span className="text-sm font-medium text-gray-700">Reason:</span>
                                <p className="text-gray-900 text-sm">{reason}</p>
                            </div>
                        )}
                        <div>
                            <span className="text-sm font-medium text-gray-700">Date:</span>
                            <p className="text-gray-900 text-sm">{new Date().toLocaleDateString()}</p>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-lg shadow-lg p-6 border border-gray-100">
                    <h3 className="text-lg font-semibold text-cinch-primary mb-3">What's Next?</h3>
                    <p className="text-gray-600 text-sm mb-4">
                        {isApproved 
                            ? 'Our team will contact you within 1-2 business days to discuss project details and timeline.'
                            : 'Feel free to reach out if you have any questions or would like to discuss alternative options.'
                        }
                    </p>
                    <a 
                        href="mailto:support@cleancinch.com" 
                        className="inline-flex items-center justify-center px-4 py-2 bg-cinch-primary text-white rounded-lg hover:bg-cinch-secondary transition-colors duration-200 text-sm font-medium"
                    >
                        Contact Us
                    </a>
                </div>
            </div>
        </div>
    );
};

export default SuccessMessage;